import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Menu, X } from 'lucide-react';

const LOGO_URL = '/logo-givinghopes.png';

export default function Navbar() {
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { to: '/', label: 'Home' },
    { to: '/about', label: 'About Us' },
    { to: '/training', label: 'Training' },
    { to: '/shop', label: 'Shop' },
    { to: '/blog', label: 'Blog' },
    { to: '/contact', label: 'Contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass-nav" data-testid="main-navbar">
      <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
          <img src={LOGO_URL} alt="Giving Hope LLC" className="h-12 w-12 object-contain" />
          <span className="text-xl font-heading font-bold text-[var(--text-headline)]">
            Giving Hope <span className="text-[var(--primary-blue)]">LLC</span>
          </span>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden lg:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`text-sm font-medium transition-colors duration-200 ${
                isActive(link.to)
                  ? 'text-[var(--primary-blue)]'
                  : 'text-[var(--text-body)] hover:text-[var(--primary-blue)]'
              }`}
              data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {link.label}
            </Link>
          ))}
        </div>

        {/* CTA Buttons */}
        <div className="hidden lg:flex items-center gap-3">
          {user ? (
            <>
              <Link to="/dashboard" className="text-sm font-semibold text-[var(--primary-blue)]" data-testid="nav-dashboard-link">
                Dashboard
              </Link>
              <button onClick={logout} className="btn-outline text-sm !px-5 !py-2" data-testid="nav-logout-btn">
                Logout
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="text-sm font-semibold text-[var(--text-body)] hover:text-[var(--primary-blue)] transition-colors" data-testid="nav-login-link">
                Login
              </Link>
              <Link to="/book-evaluation" className="btn-primary text-sm !px-5 !py-2" data-testid="nav-book-evaluation-btn">
                Book Evaluation
              </Link>
            </>
          )}
        </div>

        {/* Mobile Toggle */}
        <button
          className="lg:hidden p-2"
          onClick={() => setMobileOpen(!mobileOpen)}
          data-testid="mobile-menu-toggle"
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-[var(--border-color)] px-6 py-6 space-y-4" data-testid="mobile-menu">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className="block text-base font-medium text-[var(--text-body)]"
              onClick={() => setMobileOpen(false)}
            >
              {link.label}
            </Link>
          ))}
          <div className="pt-4 border-t border-[var(--border-color)] space-y-3">
            {user ? (
              <>
                <Link to="/dashboard" className="block btn-primary text-center text-sm" onClick={() => setMobileOpen(false)}>
                  Dashboard
                </Link>
                <button onClick={() => { logout(); setMobileOpen(false); }} className="block w-full btn-outline text-sm">
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="block text-center text-sm font-semibold text-[var(--text-body)]" onClick={() => setMobileOpen(false)}>
                  Login
                </Link>
                <Link to="/register" className="block btn-primary text-center text-sm" onClick={() => setMobileOpen(false)}>
                  Get Started
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
